// API service for activity logs
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ActivityLogService {
  /**
   * Get activity logs for the organization
   * @param {string} token - Authentication token
   * @param {number} limit - Number of records to fetch (default: 50)
   * @param {string} lastKey - Last evaluated key for pagination
   * @returns {Promise} Activity logs response
   */
  static async getActivityLogs(token, limit = 50, lastKey = null) {
    try {
      const params = new URLSearchParams({
        limit: limit.toString()
      });
      
      if (lastKey) {
        params.append('lastKey', JSON.stringify(lastKey));
      }

      const response = await fetch(`${API_BASE_URL}/activity-logs?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      throw error;
    }
  }

  /**
   * Get recent activity logs (latest 5 entries)
   * @param {string} token - Authentication token
   * @returns {Promise} Recent activity logs
   */
  static async getRecentActivityLogs(token) {
    const response = await this.getActivityLogs(token, 5);
    return response.data || [];
  }

  /**
   * Get activity logs for a specific user
   * @param {string} token - Authentication token
   * @param {string} userId - User ID to filter by
   * @param {number} limit - Number of records to fetch
   * @returns {Promise} User activity logs
   */
  static async getUserActivityLogs(token, userId, limit = 50) {
    try {
      const response = await fetch(`${API_BASE_URL}/activity-logs/user/${userId}?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user activity logs:', error);
      throw error;
    }
  }

  /**
   * Get activity logs for a specific entity
   * @param {string} token - Authentication token
   * @param {string} entityId - Entity ID to filter by
   * @param {number} limit - Number of records to fetch
   * @returns {Promise} Entity activity logs
   */
  static async getEntityActivityLogs(token, entityId, limit = 50) {
    try {
      const response = await fetch(`${API_BASE_URL}/activity-logs/entity/${entityId}?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching entity activity logs:', error);
      throw error;
    }
  }

  /**
   * Get activity logs for a specific action type
   * @param {string} token - Authentication token
   * @param {string} action - Action type to filter by (CREATE, UPDATE, DELETE, etc.)
   * @param {number} limit - Number of records to fetch
   * @returns {Promise} Action-specific activity logs
   */
  static async getActionActivityLogs(token, action, limit = 50) {
    try {
      const response = await fetch(`${API_BASE_URL}/activity-logs/action/${action}?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching action activity logs:', error);
      throw error;
    }
  }

  /**
   * Format activity log entry for display
   * @param {Object} activityLog - Activity log entry
   * @returns {Object} Formatted activity log entry
   */
  static formatActivityLogForDisplay(activityLog) {
    const actionMap = {
      'CREATE': 'Creó',
      'UPDATE': 'Actualizó',
      'DELETE': 'Eliminó',
      'UPDATE_STATUS': 'Cambió el estado de',
      'SOFT_DELETE': 'Eliminó',
      'HARD_DELETE': 'Eliminó permanentemente',
      'OVERBOOK_CREATE': 'Sobrescribió',
      'OVERBOOK_UPDATE': 'Sobrescribió'
    };

    const entityTypeMap = {
      'patient': 'paciente',
      'appointment': 'cita',
      'user': 'usuario',
      'organization': 'organización',
      'procedure': 'procedimiento',
      'supply': 'suministro',
      'payment': 'pago',
      'farmaco': 'fármaco',
      'insurance': 'seguro',
      'paymentForm': 'forma de pago',
      'provider': 'proveedor',
      'dental_note': 'odontograma',
      'prescription': 'receta',
    };

    const entityMessageMap = {
      'patient': (entity) => entity.details?.patientName || entity.entityId,
      'appointment': (entity) => entity.details?.appointmentName || entity.entityId,
      'user': (entity) => entity.details?.userName || entity.entityId,
      'organization': (entity) => entity.details?.organizationName || entity.entityId,
      'procedure': (entity) => entity.details?.procedureName || entity.entityId,
      'supply': (entity) => entity.details?.supplyName || entity.entityId,
      'payment': (entity) => entity.details?.amount || entity.entityId,
      'farmaco': (entity) => entity.details?.farmacoName || entity.entityId,
      'insurance': (entity) => entity.details?.insuranceName || entity.entityId,
      'paymentForm': (entity) => entity.details?.paymentFormName || entity.entityId,
      'provider': (entity) => entity.details?.providerName || entity.entityId,
      'dental_note': (entity) => entity.details?.dentalNoteName || entity.entityId,
      'prescription': (entity) => entity.details?.prescriptionName || entity.entityId
    };

    const iconMap = {
      'patient': 'fas fa-user',
      'appointment': 'fas fa-calendar-check',
      'user': 'fas fa-user-cog',
      'organization': 'fas fa-building',
      'procedure': 'fas fa-tooth',
      'supply': 'fas fa-box',
      'payment': 'fas fa-file-invoice-dollar',
      'farmaco': 'fas fa-pills',
      'insurance': 'fas fa-shield-alt',
      'paymentForm': 'fas fa-credit-card',
      'provider': 'fas fa-industry'
    };

    const action = actionMap[activityLog.action] || activityLog.action.toLowerCase();
    const entityType = entityTypeMap[activityLog.entityType] || activityLog.entityType;
    const icon = iconMap[activityLog.entityType] || 'fas fa-info-circle';

    // Get entity name from details if available
    const entityMessageFunction = entityMessageMap[activityLog.entityType];
    const entityMessage = entityMessageFunction ? entityMessageFunction(activityLog) : '';

    const description = `${action} ${entityType}: ${entityMessage}`.trim();

    // Calculate time ago
    const timeAgo = this.getTimeAgo(activityLog.timestamp);

    const entityDisplayName = this.getEntityDisplayName(activityLog);
    const actionDisplayName = this.getActionDisplayName(activityLog);

    return {
      id: activityLog.id,
      description,
      timeAgo,
      icon,
      entityType: activityLog.entityType,
      entityDisplayName,
      actionDisplayName,
      action: activityLog.action,
      details: activityLog.details,
    };
  }

  /**
   * Calculate time ago from timestamp
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Human-readable time ago
   */
  static getTimeAgo(timestamp) {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return 'Ahora mismo';
    } else if (diffMins < 60) {
      return `Hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
    } else if (diffDays < 7) {
      return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    } else {
      return activityTime.toLocaleDateString('es-ES');
    }
  }

  static getActionDisplayName(activityLog) {
    const actionNameMap = {
      'CREATE': 'Crear',
      'UPDATE': 'Actualizar',
      'DELETE': 'Eliminar',
      'UPDATE_STATUS': 'Cambiar Estado',
      'SOFT_DELETE': 'Eliminar',
      'OVERBOOK_CREATE': 'Sobrescribir Cita',
      'OVERBOOK_UPDATE': 'Sobrescribir Cita'
    };

    return actionNameMap[activityLog.action] || 'Acción Desconocida';
  }

  static getEntityDisplayName(activityLog) {
    const entityNameMap = {
      'patient': 'Paciente',
      'appointment': 'Cita',
      'user': 'Usuario',
      'organization': 'Organización',
      'procedure': 'Procedimiento',
      'supply': 'Suministro',
      'payment': 'Pago',
      'farmaco': 'Fármaco',
      'insurance': 'Seguro',
      'paymentForm': 'Forma de Pago',
      'provider': 'Proveedor',
      'dental_note': 'Odontograma',
    };

    return entityNameMap[activityLog.entityType] || 'Entidad Desconocida';
  }
}

export default ActivityLogService;
